import {
  EnvConfig,
  Media,
  MediaType,
  normalizeUrl,
  useAuthentication,
} from '@gripp/shared-logic';
import {
  ArrowTopRightAngleIcon,
  colors,
  SaveIcon,
  tokens
} from '@gripp/shared-ui';
import { t } from 'i18next';
import { Pressable, StyleSheet, Text, View } from 'react-native';

type FilePreviewHeaderActionsProps = {
  media: Media | null;
};

export const FilePreviewHeaderActions = ({
  media,
}: FilePreviewHeaderActionsProps) => {
  const authentication = useAuthentication();

  if (!media) return null;

  const handleAction = async () => {
    if (media.type === MediaType.Link) {
      if (media.url) {
        const normalizedUrl = normalizeUrl(media.url);
        window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    // Download document or audio
    if (media.type === MediaType.Document) {
      const documentUrl = `${EnvConfig.apiUrl}/document/${media.id}`;
      try {
        const headers: HeadersInit = {};
        if (authentication?.accessToken) {
          headers['Authorization'] = `Bearer ${authentication.accessToken}`;
        }
        const response = await fetch(documentUrl, {
          method: 'GET',
          headers: { ...headers, Accept: 'application/json' },
          credentials: 'omit',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.downloadUrl) {
            const fileResponse = await fetch(data.downloadUrl);
            if (fileResponse.ok) {
              const fileBlob = await fileResponse.blob();
              const downloadLink = document.createElement('a');
              downloadLink.href = URL.createObjectURL(fileBlob);
              downloadLink.download = media.name || 'document';
              downloadLink.style.display = 'none';
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              URL.revokeObjectURL(downloadLink.href);
              return;
            }
            window.open(data.downloadUrl, '_blank', 'noopener,noreferrer');
            return;
          }
        }
      } catch {
        // fallback to opening the URL directly
      }
      window.open(documentUrl, '_blank', 'noopener,noreferrer');
    } else {
      const fileUrl = `${EnvConfig.mediaUrl}/media/${media.id}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const isLink = media.type === MediaType.Link;

  return (
    <View style={styles.headerActions}>
      <Pressable
        onPress={handleAction}
        style={styles.actionButton}
        accessibilityRole="button"
        accessibilityLabel={isLink ? 'Open link in new tab' : 'Download file'}
      >
        {isLink ? (
          <ArrowTopRightAngleIcon size={24} color={colors.slate[700]} />
        ) : (
          <SaveIcon size={24} color={colors.slate[700]} />
        )}
      </Pressable>
    </View>
  );
};

type FilePreviewPanelProps = {
  media: Media | null;
};

export const FilePreviewPanel = ({ media }: FilePreviewPanelProps) => {
  if (!media) return null;

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Text style={[tokens.typography.title.small, styles.fileName]}>
          {media.name}
        </Text>
        <Text style={[tokens.typography.body.medium, styles.unableText]}>
          {t('asset.files.unableToPreview')}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.brand.white,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  fileName: {
    textAlign: 'center',
    color: colors.slate[800],
  },
  unableText: {
    color: colors.slate[400],
    textAlign: 'center',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: 8,
  },
  actionButton: {
    padding: 8,
    margin: 0,
    borderRadius: 8,
    height: 24,
    width: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
